import ArticleIcon from '@mui/icons-material/Article'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditIcon from '@mui/icons-material/Edit'
import FolderIcon from '@mui/icons-material/Folder'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material'
import { getUrl } from 'aws-amplify/storage'
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  ResourcesContext,
  ResourcesContextInterface,
  rootResourceFolder,
} from '../../../providers/Resources/Resources.provider'
import { themeColors } from '../../../theme'
import { AssignResourceFileModal } from './AssignResourceFileModal'
import { CreateResourceFolderModal } from './CreateResourceFolderModal'
import { DeleteResourceModal } from './DeleteResourceModal'
import { UpdateResourceFolderNameModal } from './UpdateResourceFolderNameModal'
import { UploadResourceModal } from './UploadResourceModal'

export interface ResourceFolder {
  id: number
  name: string
  parentFolderId: number | null
  type: 'folder'
}

export interface ResourceFile {
  id: number
  name: string
  storageKey: string
  folderId: number
  type: 'file'
}

export type ResourceItem = ResourceFolder | ResourceFile

export const ResourcesView: React.FC = () => {
  const navigate = useNavigate() // Add navigate hook

  const { rootResourceFolders, getRootResourceFolders, getFolderContentById } =
    useContext(ResourcesContext) as ResourcesContextInterface

  // State to track folders and files
  const [currentFolder, setCurrentFolder] = useState<ResourceFolder | null>(
    null
  )
  const [currentFolderContent, setCurrentFolderContent] = useState<
    ResourceItem[] | null
  >(null)
  const [breadcrumbPath, setBreadcrumbPath] = useState<ResourceFolder[]>([])
  const [selectedFile, setSelectedFile] = useState<ResourceItem | null>(null)
  const [uploadModalOpen, setUploadModalOpen] = useState<boolean>(false)
  const [createFolderModalOpen, setCreateFolderModalOpen] =
    useState<boolean>(false)
  const [uploadInProgress, setUploadInProgress] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false)
  const [deleteFolderOrFile, setDeleteFolderOrFile] = useState<
    rootResourceFolder | ResourceFolder | ResourceFile | null
  >(null)
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false)
  const [editFolder, setEditFolder] = useState<
    rootResourceFolder | ResourceFolder | null
  >(null)
  const [assignModalOpen, setAssignModalOpen] = useState<boolean>(false)
  const [fileToAssign, setFileToAssign] = useState<ResourceFile | null>(null)
  const [fileEdit, setFileEdit] = useState<ResourceFile | null>(null)

  // Use refs to track component mounted state to prevent memory leaks
  const isMounted = React.useRef(true)

  // Set isMounted to false when component unmounts
  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (isMounted.current) {
      getRootResourceFolders()
    }
  }, [])

  // Fetch folder contents by ID
  const fetchFolderContent = async (folderId: number) => {
    setLoading(true)
    try {
      const folderDataResponse = await getFolderContentById(folderId)

      if (isMounted.current) {
        setCurrentFolder(folderDataResponse.currentFolder)
        setCurrentFolderContent(folderDataResponse.contents)
        // Use the pre-built breadcrumb path from the backend
        setBreadcrumbPath(folderDataResponse.breadcrumbPath)
        setLoading(false)
      }
    } catch (error) {
      console.error(`Error fetching folder ${folderId}:`, error)
    }
  }

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = async (index: number) => {
    setLoading(true)
    if (index === 0) {
      if (selectedFile) {
        setSelectedFile(null)
      }
      navigate('/resources')
      await getRootResourceFolders()

      setCurrentFolder(null)
      setCurrentFolderContent(null)
      setBreadcrumbPath([])
      setLoading(false)
    } else {
      // Click on a folder in the breadcrumb path
      const folder = breadcrumbPath[index - 1]
      setBreadcrumbPath(breadcrumbPath.slice(0, -1))

      fetchFolderContent(folder.id)
    }
  }

  // Render breadcrumbs
  const renderBreadcrumbs = () => {
    return (
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize='small' />}
        aria-label='resource-navigation'
      >
        <Button
          color='inherit'
          onClick={() => handleBreadcrumbClick(0)}
          sx={{
            textTransform: 'none',
            textAlign: 'center',
            minWidth: 'auto',
            margin: 0,
            paddingX: 1,
            paddingY: 0,
          }}
        >
          <Typography>Resources</Typography>
        </Button>
        {breadcrumbPath.map((folder, index) => {
          const isLast = index === breadcrumbPath.length - 1
          return isLast ? (
            <Button
              color='inherit'
              disabled
              key={folder.id}
              sx={{
                textTransform: 'none',
                textAlign: 'center',
                minWidth: 'auto',
                margin: 0,
                paddingY: 0,
              }}
            >
              <Typography
                key={`breadcrumbPath-${folder.id}`}
                color={themeColors.gray40}
                paddingY={0}
                textAlign={'center'}
              >
                {folder.name}
              </Typography>
            </Button>
          ) : (
            <Button
              color='inherit'
              onClick={() => handleBreadcrumbClick(index + 1)}
              key={folder.id}
              sx={{
                textTransform: 'none',
                textAlign: 'center',
                minWidth: 'auto',
                margin: 0,
                paddingX: 1,
                paddingY: 0,
              }}
            >
              <Typography>{folder.name}</Typography>
            </Button>
          )
        })}
      </Breadcrumbs>
    )
  }

  // Download/view file
  const viewFile = async (file: any) => {
    try {
      const path = file.storageKey.startsWith('public/')
        ? file.storageKey
        : `public/${file.storageKey}`
      const { url } = await getUrl({ path })
      window.open(url.toString(), '_blank')
    } catch (error) {
      console.error('Error retrieving file:', error)
      alert('Error opening file. Please try again.')
    }
  }

  const buttonStyles = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyItems: 'flex-end',
    padding: 0,
    margin: '0 10px',
    width: 'max-content',
    '&:hover': {
      backgroundColor: 'rgba(241, 142, 37, 0.08)',
      borderRadius: '5px',
      padding: '4px',
    },
  }

  const iconStyles = {
    cursor: 'pointer',
  }

  // Render the resource folder/content list
  const renderResourceList = () => {
    if (loading || !rootResourceFolders) {
      return (
        <CircularProgress
          size={18}
          style={{ marginRight: 10, color: themeColors.gray50 }}
        />
      )
    }
    return (
      <List>
        {rootResourceFolders && currentFolder == null ? (
          rootResourceFolders.map((rootFolder) => (
            <Box
              key={`rootFolder-${rootFolder.id}`}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <ListItem
                button
                onClick={() => fetchFolderContent(rootFolder.id)}
              >
                <ListItemIcon>
                  <FolderIcon color='primary' />
                </ListItemIcon>
                <ListItemText primary={rootFolder.name} />
              </ListItem>

              <Button
                variant='text'
                color='warning'
                onClick={() => {
                  setEditModalOpen(true)
                  setEditFolder(rootFolder)
                }}
                sx={buttonStyles}
              >
                <EditIcon sx={iconStyles} />
                Edit
              </Button>

              <Button
                variant='text'
                color='error'
                onClick={() => {
                  setDeleteModalOpen(true)
                  setDeleteFolderOrFile(rootFolder)
                }}
                sx={buttonStyles}
              >
                <DeleteOutlineIcon color='error' sx={iconStyles} />
                Delete
              </Button>
            </Box>
          ))
        ) : currentFolderContent && currentFolderContent.length > 0 ? (
          currentFolderContent.map((folderOrFile: ResourceItem) => (
            <Box
              key={`folderOrFile-${folderOrFile.id}`}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <ListItem
                button
                key={`folderOrFile-${folderOrFile.id}`}
                onClick={() =>
                  folderOrFile.type === 'folder'
                    ? fetchFolderContent(folderOrFile.id)
                    : setSelectedFile(folderOrFile)
                }
              >
                <ListItemIcon>
                  {folderOrFile.type === 'folder' ? (
                    <FolderIcon color='primary' />
                  ) : (
                    <ArticleIcon color='primary' />
                  )}
                </ListItemIcon>
                <ListItemText primary={folderOrFile.name} />
              </ListItem>
              {folderOrFile.type === 'folder' ? (
                <Button
                  variant='text'
                  color='warning'
                  onClick={() => {
                    setEditModalOpen(true)
                    setEditFolder(folderOrFile)
                  }}
                  sx={buttonStyles}
                >
                  <EditIcon sx={iconStyles} />
                  Edit
                </Button>
              ) : (
                <>
                  <Button
                    variant='text'
                    color='info'
                    onClick={() => {
                      setAssignModalOpen(true)
                      setFileToAssign(folderOrFile)
                    }}
                    sx={buttonStyles}
                  >
                    <VisibilityIcon color='info' sx={iconStyles} />
                    Visibility
                  </Button>
                  <Button
                    variant='text'
                    color='warning'
                    onClick={() => {
                      setUploadModalOpen(true)
                      setFileEdit(folderOrFile)
                    }}
                    sx={buttonStyles}
                  >
                    <EditIcon sx={iconStyles} />
                    Edit
                  </Button>
                </>
              )}
              <Button
                variant='text'
                color='error'
                onClick={() => {
                  setDeleteModalOpen(true)
                  setDeleteFolderOrFile(folderOrFile)
                }}
                sx={buttonStyles}
              >
                <DeleteOutlineIcon color='error' sx={iconStyles} />
                Delete
              </Button>
            </Box>
          ))
        ) : (
          <ListItem>
            <ListItemText primary='Currently Empty' />
          </ListItem>
        )}
      </List>
    )
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box>{renderBreadcrumbs()}</Box>
          </Box>
          <Box>
            <Button
              variant='text'
              startIcon={<CreateNewFolderIcon color='info' />}
              onClick={() => setCreateFolderModalOpen(true)}
              sx={{ mr: 2, marginBottom: 1 }}
            >
              <Typography
                fontSize={14}
                fontWeight={700}
                color={themeColors.blue}
              >
                New Folder
              </Typography>
            </Button>
            {currentFolder ? (
              <Button
                variant='text'
                startIcon={<CloudUploadIcon color='info' />}
                onClick={() => setUploadModalOpen(true)}
              >
                <Typography
                  fontSize={14}
                  fontWeight={700}
                  color={themeColors.blue}
                >
                  Upload Resource
                </Typography>
              </Button>
            ) : null}
          </Box>
        </Box>

        {/* view file page */}
        {selectedFile ? (
          <Box sx={{ p: 2 }}>
            <Typography variant='h6' gutterBottom padding={0}>
              Viewing: {selectedFile.name}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <Button
                variant='outlined'
                color='primary'
                onClick={() => setSelectedFile(null)}
                sx={{ mt: 2, mr: 2 }}
              >
                Go Back
              </Button>
              <Button
                variant='contained'
                color='primary'
                onClick={() => viewFile(selectedFile)}
                sx={{ mt: 2 }}
              >
                Open File
              </Button>
              <Button
                variant='contained'
                color='error'
                onClick={() => {
                  setDeleteModalOpen(true)
                }}
                sx={{ mt: 2, ml: 2 }}
              >
                <Typography fontSize={13}>Delete File</Typography>
              </Button>
            </Box>
          </Box>
        ) : (
          // view folder page
          renderResourceList()
        )}
      </Paper>

      <UploadResourceModal
        currentFolder={currentFolder}
        uploadModalOpen={uploadModalOpen}
        setUploadModalOpen={setUploadModalOpen}
        uploadInProgress={uploadInProgress}
        setUploadInProgress={setUploadInProgress}
        fetchFolderContent={fetchFolderContent}
        fileEdit={fileEdit}
        setFileEdit={setFileEdit}
      />

      <CreateResourceFolderModal
        currentFolder={currentFolder}
        currentFolderContent={currentFolderContent}
        createFolderModalOpen={createFolderModalOpen}
        setCreateFolderModalOpen={setCreateFolderModalOpen}
        setLoading={setLoading}
        fetchFolderContent={fetchFolderContent}
      />

      <DeleteResourceModal
        currentFolder={currentFolder}
        deleteModalOpen={deleteModalOpen}
        setDeleteModalOpen={setDeleteModalOpen}
        deleteFolderOrFile={deleteFolderOrFile}
        setDeleteFolderOrFile={setDeleteFolderOrFile}
        loading={loading}
        setLoading={setLoading}
        fetchFolderContent={fetchFolderContent}
      />

      <UpdateResourceFolderNameModal
        currentFolder={currentFolder}
        editModalOpen={editModalOpen}
        setEditModalOpen={setEditModalOpen}
        editFolder={editFolder}
        setLoading={setLoading}
        fetchFolderContent={fetchFolderContent}
      />

      <AssignResourceFileModal
        fileToAssign={fileToAssign}
        assignModalOpen={assignModalOpen}
        setAssignModalOpen={setAssignModalOpen}
        setLoading={setLoading}
      />
    </Box>
  )
}
